import React from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import { ButtonTypes } from '@lib/components/Button/Button';
import ActionsMenu from '@lib/components/ActionsMenu/ActionsMenu';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import CountMark, { CountMarkTypes } from '@lib/components/CountMark/CountMark';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import Carousel from '@lib/components/Carousel/Carousel';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import {
  FileSizeLimits,
  UppyDocumentationAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import FilesUploader from '@lib/components/FilesUploader/FilesUploader';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import InterventionInfo from 'routes/Intervention/InterventionInfo/InterventionInfo';
import TaskPreview from 'components/TaskPreview/TaskPreview';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import getInterventionViewPageActionsMenu from '../utils/getInterventionViewPageActionsMenu';
import { UseInterventionActionsResult } from '../utils/useInterventionActions';
import { UseModalManagementResult } from '../utils/useModalManagement';
import { InterventionViewMode } from '../enums';

type Props = {
  useModalManagementResult: UseModalManagementResult;
  useInterventionActionsResult: UseInterventionActionsResult;
  isViewOnly: boolean;
};

export default function InterventionViewDetails({
  useModalManagementResult,
  useInterventionActionsResult,
  isViewOnly,
}: Props) {
  const { t } = useTranslation();
  const {
    interventionQueryHookResult,
    loading,
    onAttachFiles,
    onViewChange,
    taskQueryHookResult,
    openCreateActionPage,
  } = useInterventionActionsResult;
  const { data } = interventionQueryHookResult;
  const {
    name,
    description,
    attachmentsAttached,
    canDestroy,
    canClose,
    canUpdate,
  } = data || {};
  const isDestroyAvailable = !isViewOnly && canDestroy?.value;
  const isCloseAvailable = !isViewOnly && canClose?.value;
  const isUpdateAvailable = !isViewOnly && canUpdate?.value;
  const isCreateActionAvailable = !isViewOnly && canUpdate?.value;

  const { data: task } = taskQueryHookResult;

  const actionsMenu = getInterventionViewPageActionsMenu({
    t,
    onCreateAction: isCreateActionAvailable ? openCreateActionPage : undefined,
    onEdit: isUpdateAvailable
      ? () => onViewChange(InterventionViewMode.Edit)
      : undefined,
    onDelete: isDestroyAvailable
      ? useModalManagementResult.openInterventionDestroyModal
      : undefined,
    onClose: isCloseAvailable
      ? useModalManagementResult.openInterventionCloseModal
      : undefined,
  });

  const isMenuVisible = actionsMenu.length > 0;

  return (
    <CircleLoaderWrapper
      isLoading={loading}
      type={CircleLoaderWrapperType.blur}
    >
      <DashboardTitleHeader
        title={name}
        actions={[
          isMenuVisible ? (
            <ActionsMenu
              key="actions-menu"
              actions={actionsMenu}
              disabled={loading}
            />
          ) : undefined,
        ]}
      />
      {!!description && (
        <div className={entityViewStyles.infoItemCol}>
          <p className={entityViewStyles.infoItemTitle}>
            <MaterialIcon icon="description" symbolsOutlined />
            <Typography variant="caption">{t('description')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <TextEditor
              readOnly
              value={description}
              className={entityViewStyles.textEditor}
            />
          </div>
        </div>
      )}
      <div className={entityViewStyles.infoItemCol}>
        <div className={entityViewStyles.infoItemHeader}>
          <div className={entityViewStyles.infoItemTitle}>
            <MaterialIcon icon="attach_file" symbolsOutlined />
            <Typography
              variant="caption"
              className={entityViewStyles.infoItemLabelText}
              component="div"
            >
              {t('attachments')}
              <CountMark
                value={attachmentsAttached?.length || 0}
                type={CountMarkTypes.blue}
              />
            </Typography>
          </div>
          {!isViewOnly && (
            <FilesUploader
              // @ts-ignore
              onChange={onAttachFiles}
              value={[]}
              uploadButtonProps={{
                buttonText: t('attach'),
                buttonType: ButtonTypes.primaryOutlined,
                leftIcon: 'attach_file',
                fullWidth: true,
              }}
              allowedFileTypes={UppyDocumentationAllowedFileTypes}
              note={t('files-upload-validation-msg', {
                sizeImg: FileSizeLimits.Image,
                sizePdf: FileSizeLimits.File,
              })}
            />
          )}
        </div>
        {!!attachmentsAttached?.length && (
          <div
            className={entityViewStyles.infoItemContent}
            key={attachmentsAttached?.length}
          >
            <div className={entityViewStyles.infoItemContentFullWidth}>
              <Carousel withWrap spaceBetween={16} slidesPerView="auto">
                {attachmentsAttached.map((attachment) => (
                  <DocumentationPreview
                    key={uuidv4()}
                    attachment={attachment}
                    view={DocumentationPreviewEnum.BlockCard}
                    onClick={() =>
                      useModalManagementResult.openAttachmentPreviewModal(
                        attachment,
                      )
                    }
                  />
                ))}
              </Carousel>
            </div>
          </div>
        )}
      </div>
      {!!task?.id && (
        <div className={entityViewStyles.infoItemCol}>
          <p className={entityViewStyles.infoItemTitle}>
            <MaterialIcon icon="task_alt" symbolsOutlined />
            <Typography variant="caption">{t('linked-task')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <TaskPreview data={task} />
          </div>
        </div>
      )}
      <div className={entityViewStyles.infoItemCol}>
        <InterventionInfo
          useModalManagementResult={useModalManagementResult}
          useInterventionActionsResult={useInterventionActionsResult}
          isViewOnly={isViewOnly}
        />
      </div>
    </CircleLoaderWrapper>
  );
}
